import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Plus, Trash2, FlaskConical, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useSimulationCampaigns,
  useCreateSimulationCampaign,
  useDeleteSimulationCampaign,
  type SimulationCampaign,
} from '../api/simulations'
import { formatDateTime, cn } from '../lib/utils'

export function SimulationCampaignsPage() {
  const { t, i18n } = useTranslation('simulations')
  const { data: campaigns, isLoading } = useSimulationCampaigns()
  const createMutation = useCreateSimulationCampaign()
  const deleteMutation = useDeleteSimulationCampaign()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    setCreating(true)
    try {
      const dateLabel = new Date().toLocaleDateString(i18n.language || 'en', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      const result = await createMutation.mutateAsync({
        name: t('list.namePrefix', { date: dateLabel }),
      })
      navigate(`/app/simulations/${result.simulation_campaign.id}`)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = (e: React.MouseEvent, campaign: SimulationCampaign) => {
    e.stopPropagation()
    if (confirm(t('list.deleteConfirm', { name: campaign.name, count: campaign.total_entries }))) {
      deleteMutation.mutate(campaign.id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {t('list.subtitle')}
          </p>
        </div>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {creating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {creating ? t('list.creating') : t('list.newSimulation')}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Empty state */}
      {campaigns && campaigns.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border py-16">
          <FlaskConical className="h-12 w-12 text-text-muted" />
          <h3 className="mt-4 text-lg font-semibold text-text-primary">{t('list.emptyTitle')}</h3>
          <p className="mt-2 text-sm text-text-secondary">
            {t('list.emptySubtitle')}
          </p>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {t('list.newSimulation')}
          </button>
        </div>
      )}

      {/* Campaign cards */}
      {campaigns && campaigns.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => {
            const pct = campaign.stats.total > 0
              ? Math.round((campaign.stats.contacted / campaign.stats.total) * 100)
              : 0
            const isComplete = campaign.stats.remaining === 0 && campaign.stats.total > 0

            return (
              <div
                key={campaign.id}
                onClick={() => navigate(`/app/simulations/${campaign.id}`)}
                className="group cursor-pointer rounded-lg border border-border bg-surface-card p-5 transition-all hover:border-primary/50 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-lg',
                      isComplete ? 'bg-success/10' : 'bg-info/10'
                    )}>
                      <FlaskConical className={cn(
                        'h-5 w-5',
                        isComplete ? 'text-success' : 'text-info'
                      )} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-text-primary group-hover:text-primary">
                        {campaign.name}
                      </h3>
                      <p className="text-xs text-text-muted">
                        {t('list.simulationsCount', { count: campaign.total_entries })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, campaign)}
                    className="rounded-lg p-1.5 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover:opacity-100"
                    title={t('list.deleteCampaign')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-elevated">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        isComplete ? 'bg-success' : 'bg-info'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
                    <span>{t('list.runOf', { contacted: campaign.stats.contacted, total: campaign.stats.total })}</span>
                    <span>
                      {isComplete ? (
                        <span className="font-medium text-success">{t('list.complete')}</span>
                      ) : (
                        t('list.remaining', { count: campaign.stats.remaining })
                      )}
                    </span>
                  </div>
                </div>

                <p className="mt-3 text-xs text-text-muted">
                  {t('list.createdOn', { date: formatDateTime(campaign.created_at) })}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
